// 7.0f — the collection statement: one document per settlement of either kind
// (a web collection or a phone collection), the same paper for three readers:
// the owner on the device page (audience 'owner': every figure), the court on
// its Ball Machine page and on the public link (audience 'court': takings and
// the court's own side, never HighMark's figure — the server already withholds
// it; this file only reads what it is given).
//
// A document, not a dashboard: head, the figures, the rentals that needed a
// look, then every rental, the reconciliation note and the derived note.
// Printable: the Print button calls window.print() and the style block below
// hides the app around `.hm-statement-print`, flipping it to paper colours.
//
// Two rental shapes arrive, one per audience (the server's review item for
// the owner, the tenant rental for the court): flags are a dict of classify
// keys on one and a list of names on the other, and the side figure is
// owner_share/court_side + `derived` on one and court_side + `court_side_derived`
// on the other. `normalise()` folds both into one row for the table.
import { useEffect, useState } from 'react';
import { X, Printer } from 'lucide-react';
import { useBackdropClose } from '../utils/backdropClose';
import { GREEN, GREY, AMBER, fmtCentavos } from './KioskDialogs';
import { STOP_LABEL } from './KioskLive';
import { bonusFreeLabel } from './KioskVoucherWizard';
import { errMsg } from './kioskApi';
import { fmtHrMin, fmtBookedPlayed } from '../liveUi';

const RED = '#FF2D55';

// The flag names the court page uses, plus the owner-only ones the review item carries.
const FLAG_KEYS = [
  ['court_comp', 'court_comp'], ['owner_comp', 'owner_comp'], ['is_problem_stop', 'problem_stop'], ['is_late_stop', 'late_stop'],
  ['is_outage', 'outage'], ['is_pro_rated', 'pro_rated'], ['is_void', 'void'], ['voucher_consumed', 'voucher'],
  ['voucher_released', 'voucher_released'], ['is_forced_end', 'forced_end'], ['is_override', 'override'], ['is_mistake', 'mistake'],
];
const FLAG_LABEL = {
  court_comp: { owner: 'Court comp', court: 'Your comp' }, owner_comp: { owner: 'HighMark comp', court: 'HighMark comp' },
  problem_stop: 'Problem stop', late_stop: 'Late stop', outage: 'Outage', pro_rated: 'Pro-rated', void: 'Not charged',
  voucher: 'Voucher', voucher_released: 'Voucher returned', forced_end: 'Forced end', override: 'Owner override', mistake: 'Started by mistake',
};
const FLAG_COLOR = { court_comp: AMBER, owner_comp: AMBER, problem_stop: RED, late_stop: AMBER, outage: RED, forced_end: RED, override: AMBER, voucher: '#4FC3F7' };
const flagLabel = (f, audience) => { const l = FLAG_LABEL[f]; return !l ? f : typeof l === 'string' ? l : l[audience] || l.owner; };

const AUTH_LABEL = { owner_pin: 'owner PIN', owner_code: 'authenticator', admin_login: 'admin login' };

const hhmm = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const dmd = (ms) => (ms ? new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—');
const dmyIso = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const whenFull = (ms) => (ms ? new Date(ms).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const n = (v) => (v == null ? 0 : Number(v));
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// One row shape for both audiences (see the file head).
function normalise(r) {
  let flags = [];
  if (Array.isArray(r.flags)) flags = r.flags;
  else if (r.flags && typeof r.flags === 'object') flags = FLAG_KEYS.filter(([k]) => r.flags[k]).map(([, name]) => name);
  const derived = r.court_side_derived != null ? !!r.court_side_derived : !!r.derived;
  return {
    ...r,
    flagList: flags,
    sideDerived: derived,
    ownerSide: r.owner_share_centavos,
    courtSide: r.court_side_centavos,
    isVoid: r.status === 'VOID',
    duration: r.billed_minutes ?? r.minutes,
    // T1: "2 hrs" / "2 hrs booked · 1 min played" / "open · 45 min"; null before the server sends both figures.
    timeText: fmtBookedPlayed(r.booked_minutes, r.played_minutes, r.mode),
  };
}

// How the settlement was made, in one phrase.
function sourceLine(st) {
  if (st.source === 'phone') return 'on the phone (owner PIN)';
  const how = AUTH_LABEL[st.authorised_by] || st.authorised_by || 'admin login';
  return `on the web${st.by ? ` by ${st.by}` : ''} (${how})`;
}

/* ── Styles: screen (dark) and print (paper) ───────────────────────────────── */

const STYLE = `
.hm-statement { color: #F5F5F5; font-family: Manrope, system-ui, sans-serif; font-size: 13px; line-height: 1.5; }
.hm-statement h1, .hm-statement h2 { font-family: Oswald, sans-serif; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 800; margin: 0; }
.hm-statement h1 { font-size: 22px; }
.hm-statement h2 { font-size: 14px; margin: 22px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #2A2A2A; }
.hm-statement .hm-muted { color: #9A9A9A; }
.hm-statement .hm-dim { color: #666; }
.hm-statement .hm-eyebrow { font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #737373; font-weight: 700; }
.hm-statement table { width: 100%; border-collapse: collapse; }
.hm-statement th { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #737373; font-weight: 700; text-align: left; padding: 6px 8px; border-bottom: 1px solid #2A2A2A; }
.hm-statement td { padding: 6px 8px; border-bottom: 1px solid #1E1E1E; vertical-align: top; }
.hm-statement .hm-num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.hm-statement .hm-figures td { padding: 7px 8px; }
.hm-statement .hm-figures .hm-total td { border-top: 1px solid #2A2A2A; border-bottom: none; font-weight: 800; font-size: 15px; padding-top: 10px; }
.hm-statement .hm-note { border: 1px solid ${AMBER}55; background: ${AMBER}12; color: ${AMBER}; border-radius: 8px; padding: 8px 12px; margin-top: 14px; }
.hm-statement .hm-flag { display: inline-block; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; padding: 1px 6px; border-radius: 999px; margin: 0 4px 2px 0; border: 1px solid currentColor; }
.hm-statement .hm-wrap { overflow-x: auto; }
@media print {
  body * { visibility: hidden !important; }
  .hm-statement-overlay { position: static !important; overflow: visible !important; background: #fff !important; padding: 0 !important; display: block !important; }
  .hm-statement-print, .hm-statement-print * { visibility: visible !important; }
  .hm-statement-print { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; max-width: none !important; margin: 0 !important; padding: 12mm !important; background: #fff !important; border: none !important; border-radius: 0 !important; box-shadow: none !important; box-sizing: border-box; }
  .hm-statement, .hm-statement * { color: #000 !important; background: transparent !important; text-shadow: none !important; }
  .hm-statement .hm-muted, .hm-statement .hm-dim, .hm-statement .hm-eyebrow, .hm-statement th { color: #444 !important; }
  .hm-statement th, .hm-statement td { border-color: #bbb !important; }
  .hm-statement h2 { border-color: #000 !important; }
  .hm-statement .hm-note { border-color: #000 !important; }
  .hm-statement .hm-flag { border-color: #000 !important; }
  .hm-statement .hm-wrap { overflow: visible !important; }
  .hm-statement table { page-break-inside: auto; }
  .hm-statement tr { page-break-inside: avoid; }
  .hm-no-print { display: none !important; }
}
`;

/* ── The document ──────────────────────────────────────────────────────────── */

function Row({ label, value, sub, color, strong }) {
  return (
    <tr>
      <td className="hm-muted">{label}{sub && <span className="hm-dim"> · {sub}</span>}</td>
      <td className="hm-num" style={{ color: color || undefined, fontWeight: strong ? 800 : 600 }}>{value}</td>
    </tr>
  );
}

export function StatementView({ statement, audience = 'court' }) {
  const st = statement;
  if (!st) return null;
  const court = audience === 'court';
  const t = st.totals || {};
  const dev = st.device || {};
  const period = st.period || {};
  const rentals = (st.rentals || []).map(normalise);
  const issues = (st.issues || []).map(normalise);
  const where = [dev.name, dev.site_name, dev.court_name].filter(Boolean).join(' · ');
  const sideLabel = court ? 'Your share' : 'Court';
  const sideOf = (r) => {
    if (!r.sideDerived) return <span className="hm-dim">see settlement</span>;
    const v = r.courtSide;
    return <span style={{ color: n(v) < 0 ? AMBER : GREEN }}>{fmtCentavos(v)}</span>;
  };

  const renderTable = (rows, testId, showNote = false) => (
    <div className="hm-wrap">
      <table data-testid={testId}>
        <thead>
          <tr>
            <th>When</th><th>Duration</th><th>Ended</th><th className="hm-num">Charged</th>
            {!court && <th className="hm-num">HighMark</th>}
            <th className="hm-num">{sideLabel}</th>
            <th>Flags{showNote ? ' · note' : ''}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.row_id} data-testid={`${testId}-row-${r.row_id}`}>
              <td style={{ whiteSpace: 'nowrap' }}>{dmd(r.started_at_ms)} {hhmm(r.started_at_ms)}–{hhmm(r.ended_at_ms)}
                {/* 2026-09-30: the nickname asked before the rental — matches it to the court bookings. */}
                {r.customer_name && <div className="hm-dim" style={{ fontSize: 11 }} data-testid={`${testId}-name-${r.row_id}`}>{r.customer_name}</div>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }} data-testid={`${testId}-time-${r.row_id}`}>
                {r.timeText ?? <>{fmtHrMin(r.duration)}{r.mode ? <span className="hm-dim"> · {r.mode === 'OPEN' ? 'open' : 'fixed'}</span> : null}</>}
              </td>
              <td style={{ color: r.stop_reason ? AMBER : undefined }}>
                {r.stop_reason ? (STOP_LABEL[r.stop_reason] || r.stop_reason) : 'normal'}
                {r.bearer && r.bearer !== 'NOBODY' ? <span className="hm-dim"> · {r.bearer === 'COURT' ? (court ? 'your comp' : 'court comp') : r.bearer === 'OWNER' ? 'HighMark comp' : r.bearer.toLowerCase()}</span> : null}
              </td>
              <td className="hm-num" style={{ fontWeight: 700 }}>
                {r.trial ? <span style={{ color: GREEN }} data-testid={`${testId}-trial-${r.row_id}`}>free trial</span> : r.isVoid ? <span className="hm-dim">not charged</span> : fmtCentavos(r.charged_centavos)}
                {/* K6 P4c: a bonus voucher takes nothing off, so it names the code alone — "−₱0.00" would be noise. */}
                {r.voucher?.consumed && !r.trial && <div className="hm-dim" style={{ fontSize: 11, fontWeight: 400 }}>voucher {r.voucher.code}{r.voucher.discount_centavos > 0 ? ` −${fmtCentavos(r.voucher.discount_centavos)}` : ''}</div>}
                {/* K6 P4b — the court booking the voucher was claimed on; the server joins it by claim id. */}
                {r.voucher && r.court_booking_ref && <div className="hm-dim" style={{ fontSize: 11, fontWeight: 400 }} data-testid={`${testId}-court-booking-${r.row_id}`}>court booking {r.court_booking_ref}</div>}
                {/* K6 P4c — the free machine time the voucher gave (voucher_bonus_minutes); it moves no money, the charge is for the paid minutes. */}
                {bonusFreeLabel(r.voucher_bonus_minutes) && <div className="hm-dim" style={{ fontSize: 11, fontWeight: 400 }} data-testid={`${testId}-bonus-${r.row_id}`}>{bonusFreeLabel(r.voucher_bonus_minutes)}{r.voucher_bonus_value_centavos > 0 ? ` (worth ${fmtCentavos(r.voucher_bonus_value_centavos)})` : ''}</div>}
                {r.late_stop_forgone_centavos > 0 && <div className="hm-dim" style={{ fontSize: 11, fontWeight: 400 }}>{fmtCentavos(r.late_stop_forgone_centavos)} forgone</div>}
              </td>
              {!court && <td className="hm-num" style={{ color: AMBER }}>{r.sideDerived ? fmtCentavos(r.ownerSide) : <span className="hm-dim">?</span>}</td>}
              <td className="hm-num">{sideOf(r)}</td>
              <td>
                {r.flagList.map(f => <span key={f} className="hm-flag" style={{ color: FLAG_COLOR[f] || GREY }}>{flagLabel(f, audience)}</span>)}
                {showNote && r.note && <div className="hm-muted" style={{ fontSize: 12 }}>{r.note}</div>}
                {showNote && Array.isArray(r.attention_reasons) && r.attention_reasons.length > 0 && <div className="hm-dim" style={{ fontSize: 11 }}>{r.attention_reasons.join(', ')}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="hm-statement" data-testid="kiosk-statement" data-source={st.source} data-audience={audience}>
      <style>{STYLE}</style>

      {/* Head */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <p className="hm-eyebrow">HighMark · Ball machine</p>
          <h1>Settlement statement</h1>
          <p className="hm-muted" style={{ marginTop: 2 }} data-testid="kiosk-statement-where">{where || 'Rental kiosk'}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p className="hm-eyebrow">Period</p>
          <p style={{ fontSize: 15, fontWeight: 700 }} data-testid="kiosk-statement-period">
            {dmyIso(period.from)} – {dmyIso(period.to)}
          </p>
          <p className="hm-muted" style={{ fontSize: 12 }}>{period.days != null ? plural(period.days, 'day') : ''}{st.prior?.settled_through ? ` · after the settlement through ${dmyIso(st.prior.settled_through)}` : ' · first settlement'}</p>
        </div>
      </div>

      <table className="hm-figures" style={{ marginTop: 14 }} data-testid="kiosk-statement-head">
        <tbody>
          <Row label="Collected" value={whenFull(st.collected_at_ms)} />
          <Row label="Settled" value={sourceLine(st)} />
          {st.source === 'web' && <Row label="Applied by the phone" value={st.acked_by_phone ? 'yes' : 'not yet'} color={st.acked_by_phone ? GREEN : AMBER} />}
          {st.note ? <Row label="Note" value={st.note} /> : null}
          <Row label="Reference" value={st.id} />
        </tbody>
      </table>

      {st.conflict && (
        <p className="hm-note" style={{ borderColor: RED + '66', background: RED + '12', color: RED }} data-testid="kiosk-statement-conflict">
          Double settlement: this phone settlement covers rentals already settled on the web. Both records stand; the difference is settled by hand.
        </p>
      )}

      {/* Figures */}
      <h2>Figures</h2>
      <table className="hm-figures" data-testid="kiosk-statement-figures">
        <tbody>
          <Row label="Rentals" value={n(t.rentals)} sub={`${n(t.charged_count)} charged · ${n(t.void_count)} not charged`} />
          {/* T1: Booked time and Time played, the kiosk's own words; the money-derived "billed h" is retired. */}
          {t.booked_minutes != null && t.played_minutes != null ? (
            <>
              <tr data-testid="kiosk-statement-booked"><td className="hm-muted">Booked time</td><td className="hm-num" style={{ fontWeight: 600 }}>{fmtHrMin(t.booked_minutes)}</td></tr>
              <tr data-testid="kiosk-statement-played"><td className="hm-muted">Time played</td><td className="hm-num" style={{ fontWeight: 600 }}>{fmtHrMin(t.played_minutes)}</td></tr>
            </>
          ) : <Row label="Hours" value={fmtHrMin(t.minutes)} />}
          <Row label="Customers paid" value={fmtCentavos(t.gross_centavos)} strong />
          {/* K6 P4c: a period of only bonus vouchers discounted nothing — no Discounts row.
              T1: the count is only the vouchers that took money off (voucher_discount_count). */}
          {n(t.vouchers) > 0 && n(t.voucher_discount_centavos) > 0 && (
            <Row label="Discounts" value={`−${fmtCentavos(t.voucher_discount_centavos)}`}
              sub={`${plural(n(t.voucher_discount_count ?? t.vouchers), 'voucher')} · ${court ? 'you funded' : 'court funded'} ${fmtCentavos(t.voucher_court_funded_centavos)} · HighMark funded ${fmtCentavos(t.voucher_owner_funded_centavos)}`} />
          )}
          {/* T1: bonus-time vouchers moved no money; they gave machine time — "2 free-time vouchers · 1 hr free". */}
          {n(t.voucher_bonus_count) > 0 && (
            <Row label="Free time" value={`${fmtHrMin(n(t.voucher_bonus_minutes))} free`} sub={plural(n(t.voucher_bonus_count), 'free-time voucher')} />
          )}
          {/* 2026-09-30: free trials moved no money for anyone — how many, and the machine time they used. */}
          {n(t.trial_count) > 0 && (
            <Row label="Free trials" value={`${fmtHrMin(n(t.trial_minutes))} played`} sub={plural(n(t.trial_count), 'free trial')} />
          )}
          {(n(t.court_comps) > 0 || n(t.court_comp_owed_centavos) !== 0 || n(t.owner_comp_owed_centavos) !== 0) && (
            <Row label="Comps" value={plural(n(t.court_comps), court ? 'comp of yours' : 'court comp')}
              sub={[
                n(t.court_comp_owed_centavos) ? `${court ? 'you owe' : 'the court owes'} ${fmtCentavos(t.court_comp_owed_centavos)} on them` : null,
                !court && n(t.owner_comp_owed_centavos) ? `HighMark owes ${fmtCentavos(t.owner_comp_owed_centavos)} on its own` : null,
              ].filter(Boolean).join(' · ') || null} />
          )}
          {n(t.late_stops) > 0 && (
            <Row label="Late stops" value={n(t.late_stops)} sub={`${fmtCentavos(t.late_stop_forgone_centavos)} not charged, counted on both sides`} color={AMBER} />
          )}
          {n(t.problem_stops) > 0 && <Row label="Problem stops" value={n(t.problem_stops)} />}
          {n(t.pro_rated_count) > 0 && <Row label="Pro-rated" value={n(t.pro_rated_count)} />}
          {n(t.underivable) > 0 && <Row label="Not derivable" value={n(t.underivable)} sub="rows without a split rule; the phone's settlement decides" color={AMBER} />}
          {court ? (
            <Row label="Your share" value={fmtCentavos(t.court_centavos)} color={n(t.court_centavos) < 0 ? AMBER : GREEN} strong />
          ) : (
            <>
              <Row label="HighMark's share" value={fmtCentavos(t.owner_centavos)} color={AMBER} strong />
              <Row label="Court's share" value={fmtCentavos(t.court_centavos)} color={n(t.court_centavos) < 0 ? RED : GREEN} strong />
            </>
          )}
        </tbody>
      </table>
      {n(t.court_centavos) < 0 && (
        <p className="hm-muted" style={{ fontSize: 12, marginTop: 6 }}>
          {court ? 'A negative share means you owe HighMark this on top of handing over the cash.' : 'Negative court side: the court owes the owner this on top of handing over the cash.'}
        </p>
      )}

      {st.reconciled === false && (
        <p className="hm-note" data-testid="kiosk-statement-unreconciled">
          The phone&apos;s rows for this period are still arriving; figures may change.
          {st.session_count != null && <span> The settlement recorded {plural(n(st.session_count), 'rental')} and {fmtCentavos(st.gross_centavos)} paid; the rows here so far add up to {plural(n(t.rentals), 'rental')} and {fmtCentavos(t.gross_centavos)}.</span>}
        </p>
      )}

      {/* Issues */}
      <h2>Needed a look</h2>
      {issues.length === 0
        ? <p className="hm-muted" data-testid="kiosk-statement-no-issues">Nothing in this period needed a look.</p>
        : renderTable(issues, 'kiosk-statement-issues', true)}

      {/* Every rental */}
      <h2>Every rental</h2>
      {rentals.length === 0
        ? <p className="hm-muted" data-testid="kiosk-statement-no-rentals">No rental rows are on the server for this settlement yet.</p>
        : renderTable(rentals, 'kiosk-statement-rentals')}

      <p className="hm-dim" style={{ fontSize: 11, marginTop: 18, lineHeight: 1.5 }}>{st.derived_note}</p>
      <p className="hm-dim" style={{ fontSize: 11, marginTop: 2 }}>The machine&apos;s own record is the settlement; this statement reads it back.</p>
    </div>
  );
}

/* ── The overlay ───────────────────────────────────────────────────────────── */

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #2A2A2A', borderRadius: 8,
  padding: '7px 12px', color: '#ccc', cursor: 'pointer', fontSize: 12, fontWeight: 700,
};

// Fetches through `load()` (a promise for the statement) and shows it full-screen.
export function StatementModal({ title = 'Statement', load, audience = 'court', onClose }) {
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState('');
  const backdrop = useBackdropClose(onClose);

  useEffect(() => {
    let live = true;
    setStatement(null);
    setError('');
    Promise.resolve().then(load)
      .then(s => { if (live) setStatement(s); })
      .catch(e => { if (live) setError(errMsg(e, 'Could not load this statement')); });
    return () => { live = false; };
  }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[70] hm-statement-overlay" style={{ background: 'rgba(0,0,0,0.9)', overflowY: 'auto', padding: 16 }} {...backdrop} data-testid="kiosk-statement-modal">
      <div className="hm-statement-print" onClick={e => e.stopPropagation()}
        style={{ background: '#141414', border: '1px solid #2A2A2A', borderRadius: 16, width: '100%', maxWidth: 900, margin: '16px auto', padding: 24, color: '#fff' }}>
        <div className="hm-no-print flex items-center justify-between gap-3" style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #1E1E1E' }}>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</h2>
          <div className="flex gap-2">
            <button onClick={() => window.print()} disabled={!statement} data-testid="kiosk-statement-print" style={{ ...btn, opacity: statement ? 1 : 0.5 }}><Printer className="h-4 w-4" /> Print</button>
            <button onClick={onClose} data-testid="kiosk-statement-close" style={btn}><X className="h-4 w-4" /> Close</button>
          </div>
        </div>
        {error ? <p style={{ color: RED, fontSize: 13 }} data-testid="kiosk-statement-error">{error}</p>
          : !statement ? <p style={{ color: GREY, fontSize: 13, textAlign: 'center', padding: 40 }}>Loading the statement…</p>
          : <StatementView statement={statement} audience={audience} />}
      </div>
    </div>
  );
}
